import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { useSettingsList } from "@/modules/settings/hooks/useSettings";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { notificationsService } from "../services/notifications.service";
import type { NotificationChannelsMap, NotificationItem } from "../types";

export function useNotificationsList(unread: boolean) {
	return usePaginatedQuery<NotificationItem>({
		queryKey: ["notifications", unread],
		url: "/notifications",
		// ⚠️ `unread: false` YUBORILMAYDI: backend `z.enum(["true","false"])`
		// kutadi va `false` qiymati «hammasi» degani — parametrni tashlab
		// yuborish aynan shuni bildiradi.
		params: unread ? { unread: "true" } : {},
	});
}

export function useMarkNotificationRead() {
	const { t } = useTranslation("notifications");
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => notificationsService.markRead(id),
		// Kalit prefiksi `["notifications"]` — `usePaginatedQuery` uni
		// `[...queryKey, page, limit, params]` bilan kengaytiradi, ya'ni
		// prefiks bo'yicha invalidatsiya barcha sahifa/filtr variantlariga
		// tegadi.
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
		onError: (error) => {
			message.error(getErrorMessage(error, t("messages.markReadError")));
		},
	});
}

/**
 * `notification_channels` sozlamasi — mavjud `/settings` ro'yxatidan.
 *
 * ⚠️ `setting` obyektining O'ZI qaytariladi (`?? {}` bilan yangi obyekt
 * YASALMAYDI) — chaqiruvchi uni `useEffect` bog'liqligi sifatida ishlatadi
 * va har renderda yangi havola cheksiz siklga olib kelardi (`SettingsPage`
 * da o'lchangan regressiya).
 */
export function useNotificationChannels() {
	const query = useSettingsList();
	const setting = query.data?.find((item) => item.key === "notification_channels");

	return { setting, isLoading: query.isLoading, isError: query.isError };
}

export function useSaveNotificationChannels() {
	const { t } = useTranslation("notifications");
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (value: NotificationChannelsMap) => notificationsService.saveChannels(value),
		onSuccess: () => {
			message.success(t("channels.saveSuccess"));

			return queryClient.invalidateQueries({ queryKey: ["settings"] });
		},
		onError: (error) => {
			message.error(getErrorMessage(error, t("channels.saveError")));
		},
	});
}

/**
 * Bot TOKEN holati — `notification_channels` bilan bir xil kesh kaliti
 * EMAS, o'z so'rovi. Javob endi bot `@username`ini ham o'z ichiga oladi
 * (tokendan `getMe` orqali avtomatik aniqlangan, `rotateTelegramBotToken()`),
 * shuning uchun alohida username sozlamasi/hook'i endi YO'Q.
 */
export function useTelegramBotTokenStatus() {
	return useQuery({
		queryKey: ["settings", "telegram-bot-token"],
		queryFn: () => notificationsService.getTelegramBotTokenStatus(),
	});
}

export function useRotateTelegramBotToken() {
	const { t } = useTranslation("notifications");
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (token: string) => notificationsService.rotateTelegramBotToken(token),
		onSuccess: () => {
			message.success(t("channels.telegramTokenSaved"));

			return queryClient.invalidateQueries({ queryKey: ["settings", "telegram-bot-token"] });
		},
		onError: (error) => {
			message.error(getErrorMessage(error, t("channels.telegramTokenSaveError")));
		},
	});
}
